import React, { useEffect, useRef, useState } from 'react'

import { useMap } from '../maphooks/useMap'
import { useLayers } from '../maphooks/maphooks/layers/useLayers'
import { useLegends } from '../maphooks/maphooks/useLegends'
import { useSelection } from '../maphooks/maphooks/useSelection'

import generateSources from '../layers/sources'
import generateDisplayLayers from '../layers/layer_colors'
import { all_protos } from '../maphooks/maphooks/layers/protos/all_protos'
import Legend from '../legends/legend'
import { init_viewport, init_layer, active_dataset } from '../data/startup_data'
import SustLogo from '../assets/white-logo-small.png'
import { authContext } from "../context/authContext";
import { useContext } from "react";

import { _BarChart as BarChart } from '../panels/stats_panel'
import DropdownMenu from '../panels/dropdown_menu'
import { tile_builder_url, ce_url } from '../config'
import '../App.css';

// import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

function Map({ project }) {
    useEffect(() => {
        document.title = "Natural Climate";
    }, []);

    const user = useContext(authContext);
    function transformRequest(url, resourceType) {
        if (resourceType === 'Tile' && url.includes(ce_url)) {
            return {
                url: url, headers: { 
                    'X-SustGlobal-APIKey': "",
                    'X-SustGlobal-Project': `${project}`,
                }, // credentials: 'include'  // Include cookies for cross-origin requests)
            }
        }
    }

    const {
        map, mapContainer, mapLoaded, viewport, style, setStyle, setViewport, flyToViewport
    } = useMap(init_viewport, 'mapbox://styles/mapbox/satellite-v9', // 'mapbox://styles/mapbox/dark-v10',
        'pk.eyJ1Ijoic3VzdGRldmVsb3BlciIsImEiOiJja21tOWJhZjgxaWtmMndtdXdyMng5bGpsIn0.CZlLg_X0_Gp3hqKYRyJ_uw', transformRequest)

    const nameMapping = {
        'wildfire_ai': 'Wildfire Probability',
        'carbon_percent_loss': 'Wildfire-Driven Carbon Loss Fraction',
        'pga': 'Earthquake',
        'pest': 'Pests',
        'water_balance': 'Water Balance',
        'fire_obs': 'Observed Wildfire',
        'extreme_precip': 'Extreme Precipitation',
        'cyclone': 'Cyclone Probability',
        'cyclone_obs': 'Observed Cyclones',
        'slr': 'Sea Level Rise'
    }

    const typeMapping = {
        "Blue Carbon": ["extreme_precip", "cyclone", "cyclone_obs", "slr"],
        "Forest Carbon": ["wildfire_ai", "carbon_percent_loss", "pga", "pest", "water_balance", "fire_obs"]
    }

    const downloadFile = (user, selectedProject, selectedDataset) => {
        user && user.getIdTokenResult()
            .then((token) => {
                if (!!token.claims.api_key) {
                    fetch(`${ce_url}/api/portfolios/ncs/${selectedDataset.portfolio_name}/export`, {
                        method: 'GET',
                        headers: {
                            'X-SustGlobal-APIKey': token.claims.api_key,
                            'X-SustGlobal-Project': `${selectedProject}`,
                        },
                    })
                        .then((response) => response.blob())
                        .then((blob) => {
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${selectedDataset.portfolio_name}-nc.json`;
                            document.body.appendChild(link);
                            link.click();
                            link.remove()
                            window.URL.revokeObjectURL(url);
                        })
                        .catch((error) => {
                            console.error(`error while fetching from API: ${error}`)
                        });
                } else {
                    console.error('api key not available for user')
                }
            })
            .catch((error) => {
                console.error(`error while fetching from API: ${error}`)
            })
    }

    const [allDatasets, setAllDatasets] = useState([])
    const [activeDataset, setActiveDataset] = useState()
    const [availableLayers, setAvailableLayers] = useState({})
    const [displayedLayers, setDisplayedLayers] = useState([])
    const [previousLayer, setPreviousLayer] = useState("wildfire_ai")
    const [sources, setSources] = useState([])

    useEffect(() => {
        console.log("Current project:", project)
        user && project && user.getIdTokenResult()
            .then((token) => {
                if (!!token.claims.api_key) {
                    fetch(`${ce_url}/api/portfolios/ncs/grid`, {
                        method: 'GET',
                        headers: {
                            'X-SustGlobal-APIKey': token.claims.api_key,
                            'X-SustGlobal-Project': project,
                        }
                    })
                        .then(r => r.json())
                        .then(r => setAllDatasets(r))
                } else {
                    console.error('api key not available for user')
                }
            })
            .catch((error) => {
                console.log(error)
            })
    }, [project])

    const {
        currentLayer, layerSelectionDependencies, layerVisibilityDependencies, setCurrentLayer
    } = useLayers(map, mapLoaded, style, availableLayers, sources, activeDataset)

    const {
        selectedFeatures, selectableLayers
    } = useSelection(map, mapLoaded, mapContainer, activeDataset, layerSelectionDependencies, layerVisibilityDependencies, currentLayer)

    const {
        legends
    } = useLegends(currentLayer, mapLoaded, availableLayers, all_protos)

    useEffect(() => {
        user.getIdTokenResult()
            .then((token) => {
                if (!!token.claims.api_key) {
                    if (!mapLoaded) return
                    if (activeDataset) {
                        setTimeout(() => {
                            setSources(generateSources(activeDataset))
                        }, 1000)
                        setPreviousLayer(currentLayer)
                        setCurrentLayer("") // Essential to ensure swapping projects loads correctly 

                        const b = activeDataset.bounds
                        map.flyToBounds([[b[0], b[1]], [b[2], b[3]]])
                    }
                } else {
                    console.error('api key not available for user')
                }
            })
            .catch((error) => {
                console.log(error)
            })
    }, [activeDataset, mapLoaded, map])

    useEffect(() => {
        if (activeDataset && sources) {
            setAvailableLayers(generateDisplayLayers(activeDataset))
        }
    }, [sources])

    useEffect(() => {
        if (availableLayers && Object.keys(availableLayers).length > 0) {
            setDisplayedLayers(Object.keys(availableLayers).map(l => nameMapping[l]));
            if (!typeMapping[activeDataset.persona].includes(previousLayer)) {
                setCurrentLayer(activeDataset.persona === 'Blue Carbon' ? 'cyclone' : 'wildfire_ai');
            } else {
                setCurrentLayer(previousLayer)
            }
        }
    }, [availableLayers])

    useEffect(() => {
        let element = document.getElementById("layers");
        if (currentLayer && element.value !== currentLayer) {
            element.value = nameMapping[currentLayer];
        }
    }, [displayedLayers, currentLayer])

    return (<div className="App">
        <div ref={mapContainer} className="map-container"></div>
        <div className="filter-panel">
            <div className="filter-container">
                <div className="selector-container">
                    <DropdownMenu
                        style={{ width: "260px" }}
                        options={allDatasets.filter(d => d.status === 'Risk data available').map(d => d.portfolio_name)}
                        onSelect={(selected) => setActiveDataset(allDatasets.find(d => d.portfolio_name === selected))}
                        label={"Nature Project"}
                        id={"dataset"}
                    />
                </div>
                <div className="selector-container">
                    <DropdownMenu
                        style={{ width: "260px" }}
                        options={displayedLayers}
                        onSelect={(selected) => setCurrentLayer(Object.keys(nameMapping).find(key => nameMapping[key] === selected))}
                        label={"Risk Layers"}
                        id={"layers"}
                    />
                </div>
                <div className="selector-container">
                    <button className="summary-button" onClick={() => downloadFile(user, project, activeDataset)}>Download</button>
                </div>
            </div>
        </div>
        <BarChart
            data={selectedFeatures}
            selectableLayers={selectableLayers}
            activeDataset={activeDataset}
            currentLayer={currentLayer} />
        <Legend legend_items={legends} />
        {/*<img src={SustLogo} className='sust-logo' />*/}
    </div>);
}

export default Map;
